/**
 * Hook to load questionários for selection in Template Editor
 *
 * Used by TemplateEditorPage to allow user to select a questionário
 * for preview with real grupos data
 */

import { useQuery } from "@tanstack/react-query";
import { getQuestionarios } from "@/modules/diagnosticos/firestore";
import useUserStore from "@/modules/users/store";

const QUESTIONARIOS_SELECT_QUERY_KEY = "questionarios-select";

export interface QuestionarioOption {
  id: string;
  nome: string;
  descricao?: string;
}

/**
 * Hook para carregar questionários disponíveis para seleção
 * @returns Query com lista de questionários ordenados por nome
 */
export function useQuestionarioSelect() {
  const empresaId = useUserStore((state) => state.activeEmpresaId);

  const questionariosQuery = useQuery({
    queryKey: [QUESTIONARIOS_SELECT_QUERY_KEY, empresaId],
    queryFn: async (): Promise<QuestionarioOption[]> => {
      if (!empresaId) {
        return [];
      }

      // Use existing getQuestionarios function from diagnosticos module
      const snapshot = await getQuestionarios(empresaId);

      if (snapshot.empty) {
        return [];
      }

      const questionarios = snapshot.docs.map((document_) => {
        const data = document_.data();
        return {
          id: document_.id,
          nome: data.nome || "Sem nome",
          descricao: data.descricao
        };
      });

      // Sort in memory by nome
      return questionarios.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    },
    enabled: !!empresaId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2
  });

  return {
    questionarios: questionariosQuery.data ?? [],
    isLoading: questionariosQuery.isLoading,
    error: questionariosQuery.error
  };
}
